/**
 * DashboardPayments entity class
 */

import { PAYMENT_SYSTEM_TYPE_TO_CARD, filterSystemTypes } from 'app/core/constants'

/**
 * DashboardPaymentsFactory
 * returns DashboardPayments entity class
 *
 * @class DashboardPaymentsFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.DashboardPayments} class
 *
 * @ngdoc factory
 * @name mwApp.factory:DashboardPaymentsFactory
 */
/* @ngInject */
export default function DashboardPaymentsFactory ($http, Entity) {
  /* === DashboardPayments class === */

  /**
   * DashboardPayments model
   *
   * @class DashboardPayments
   * @classdesc DashboardPayments model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @return {models.DashboardPayments} DashboardPayments entity
   */
  function DashboardPayments () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  DashboardPayments.prototype = Object.create(Entity.prototype)
  DashboardPayments.prototype.constructor = DashboardPayments

  /* === DashboardPayments instance methods & fields === */

  Object.assign(DashboardPayments.prototype, {

    /**
     * instance fields
     *
     * @memberof models.DashboardPayments#
     * @type {Object}
     */
    _fields: {
      // metrics keyed by dashboard card setting key, e.g. { ALL_PAYMENTS_CARD: [...] }
      paymentsCountByCard: {},

      autopayPaymentsGraph: [],
      paymentsBySystemTypeGraph: []
    },

    /**
     * url template
     *
     * @memberof models.DashboardPayments#
     * @type {String}
     */
    _urlTemplate: 'api/dashboard/widgets/payments',

    /**
     * public url template
     *
     * @memberof models.DashboardPayments#
     * @type {String}
     */
    _publicUrlTemplate: 'dashboard/widgets/payments',

    /**
     * Get service for payment
     *
     * @memberof models.DashboardPayments#
     * @private
     */
    _loadAggregatePayment: function (params, callback) {
      return DashboardPayments.load({
        params,
        url: '\\agg'
      }, { valueUnit: null, value: 0 })
        .then(({ valueUnit, value }) => callback.call(this, { valueUnit, value }))
    },

    /**
     * Get service for payment
     *
     * @memberof models.DashboardPayments#
     * @private
     */
    _loadAggregateGraphPayment: function (params) {
      return DashboardPayments.load({
        params,
        url: '\\agg\\graph'
      }, { graphView: null })
    },

    /**
     * Returns metrics for a given currency, aggregated by payment system types.
     * Results are stored in `paymentsCountByCard` keyed by dashboard card setting
     * key (e.g. ALL_PAYMENTS_CARD, GR4VY_PAYMENTS_CARD).
     *
     * Aggregate cards (Total / Autopay) are always queried when enabled. System-
     * specific cards are queried only when both enabled in settings AND their
     * payment system type is available for the provider — so types disabled in
     * Odin are never requested even if a stale localStorage setting enables them.
     *
     * @param currencyId
     * @param {DashboardSetup} settings
     * @param period
     * @param {Array<String>} [availableTypes] Odin-filtered available payment system types
     * @return {Promise}
     */
    loadMetrics: function (currencyId, settings, period, availableTypes = []) {
      const params = { 'agg-type': 'SUM', currencyId }
      const results = []
      this.paymentsCountByCard = {}

      const pushMetric = (cardKey, extraParams) => {
        this.paymentsCountByCard[cardKey] = []
        results.push(this._loadAggregatePayment({
          ...params,
          ...extraParams,
          days: period
        }, (payload) => { this.paymentsCountByCard[cardKey].push(payload) }))
      }

      // Aggregate cards: not tied to a single PaymentSystemType, always available.
      settings.AUTOPAY_PAYMENTS_CARD && settings.AUTOPAY_PAYMENTS_CARD.enabled &&
        pushMetric('AUTOPAY_PAYMENTS_CARD', { autoPay: true })

      settings.ALL_PAYMENTS_CARD && settings.ALL_PAYMENTS_CARD.enabled &&
        pushMetric('ALL_PAYMENTS_CARD', {})

      // System-specific cards: only for types available for the provider.
      const available = filterSystemTypes(Object.keys(PAYMENT_SYSTEM_TYPE_TO_CARD), availableTypes)
      for (const type of available) {
        const cardKey = PAYMENT_SYSTEM_TYPE_TO_CARD[type]
        settings[cardKey] && settings[cardKey].enabled &&
          pushMetric(cardKey, { 'system-type': type })
      }

      return Promise.all(results)
    }
  })

  /* === DashboardPayments static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(DashboardPayments, Entity)
  DashboardPayments._name = 'DashboardPayments'
  DashboardPayments.init()

  /**
   * Load extra for entity with filter payload
   *
   * @param url
   */
  DashboardPayments.loadPayloadExtra = (url) =>
    DashboardPayments.loadExtra(url)

  return DashboardPayments
}
